import Tabs from "./tabs";
import { useState, useEffect } from "react";
import { Box, Button, Grid, Card, CardContent, Stack, Typography } from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FieldSelect, FieldTextField } from "../../../components/selections/fieldInputs";
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import ApartmentIcon from '@mui/icons-material/Apartment';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import SnackbarMessage from "../../../components/snackBar/snackBar";
import { useSelector, useDispatch } from "react-redux";
import qrcode from 'qrcode';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { uploadFileThunk } from "../../../components/general/generalThunk";
import { generateQrCodeThunk } from "../qrCodeThunk";
import { APP_ROUTES } from "../../../config/appRoutes";
import { useNavigate } from "react-router-dom";
import { getQrCodeThunk } from "../qrCodeThunk";
import { QR, qrCardSx, QrActionButton } from "./qrUi";

// Create-QR form (fields, validation, upload + create, live preview).
// Used by the /qr-creation page and by the "Create QR" popup on the list page.
// onSuccess(message) is called after the QR is created and the list is refreshed.
export function QrCreateForm({ onSuccess, onCancel, embedded = false }) {
    // Popup only: success text shown below the Create QR Code button (form stays open for the next QR)
    const [successMsg, setSuccessMsg] = useState("");
    useEffect(() => {
        if (!successMsg) return undefined;
        const t = setTimeout(() => setSuccessMsg(""), 5000);
        return () => clearTimeout(t);
    }, [successMsg]);
    const dispatch = useDispatch();

    const [state, setState] = useState({
        qrCodeName: "",
        qrCodeKey: "",
        departmentValue: "",
        branchValue: "",
        tag_id: "",
        open: false,
        message: "",
        severity: "error",
        errors: {},
        branchDepartmentId: "",
    });

    const { status, error, branchDepartments } = useSelector((state) => state.general);

    const { user } = useSelector((state) => state.authentication);
    const { getTags } = useSelector((state) => state.contacts);

    const handleChange = (eOrName, maybeValue) => {
        if (successMsg) setSuccessMsg("");
        if (typeof eOrName === "string") {
            const name = eOrName;
            const value = maybeValue;
            setState((prev) => ({
                ...prev,
                [name]: value,
                errors: { ...prev.errors, [name]: "" },
            }));
        } else {
            const { name, value } = eOrName.target;

            let qrvalue = "";
            const emojiRegex = /(©|®|[ -㌀]|\ud83c[퀀-\udfff]|\ud83d[퀀-\udfff]|\ud83e[퀀-\udfff])/g;
            if (name === "qrCodeName") {
                qrvalue = value.replace(emojiRegex, '');
            } else {
                qrvalue = value.replace(/^\s+/, "").replace(/[^a-z0-9_,A-Z]/ig, '');
            }

            if (qrvalue.startsWith(" ")) return;
            setState((prev) => ({
                ...prev,
                [name]: qrvalue,
                errors: { ...prev.errors, [name]: "" },
            }));
        }
    };


    const fields = [
        { id: 1, type: "text", name: "qrCodeName", label: "QR Code Name" },
        { id: 2, type: "text", name: "qrCodeKey", label: "QR Code Key" },
        { id: 3, type: "select", name: "branchDepartmentId", label: "Branch Department", options: branchDepartments.map((item) => ({ value: item.id, label: item.branch_department_name })) },
        { id: 4, type: "select", name: "tag_id", label: "Tag", options: getTags.map((tag) => ({ value: tag.id, label: tag.tag_name })) },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validations = [
            { valid: !!state.qrCodeName, message: "QR Code Name is required", field: "qrCodeName" },
            { valid: !!state.qrCodeKey, message: "QR Code Key is required", field: "qrCodeKey" },
            { valid: !!state.branchDepartmentId, message: "Please select a branch Department", field: "branchDepartmentId" },
            { valid: !!state.tag_id, message: "Please select a tag", field: "tag_id" },

        ];

        for (let rule of validations) {
            if (!rule.valid) {
                setState((prev) => ({
                    ...prev,
                    open: true,
                    message: rule.message,
                    severity: "error",
                    errors: { ...prev.errors, [rule.field]: rule.message },
                }));
                return;
            }
        }

        let whatsappUrl = "";
        whatsappUrl = `https://api.whatsapp.com/send?phone=${user?.sender_mobile}&text=${encodeURIComponent(state.qrCodeKey)}`;
        const image = await qrcode.toDataURL(whatsappUrl);
        const file = [{ "filename": state.qrCodeName, "img": image }];

        try {
            const uploadResult = await dispatch(
                uploadFileThunk({ credentials: { file, category: "qr" } })
            );

            if (
                uploadResult.meta.requestStatus !== "fulfilled" ||
                !uploadResult.payload?.data?.length
            ) {
                setState((prev) => ({
                    ...prev,
                    open: true,
                    message: uploadResult.payload?.message || "File upload failed",
                    severity: "error",
                }));
                return;
            }

            const fileUrl = uploadResult.payload.data[0].file_url;

            const qrResult = await dispatch(
                generateQrCodeThunk({
                    credentials: {
                        qr_code_name: state.qrCodeName,
                        qr_code_key: state.qrCodeKey,
                        branch_department_id: state.branchDepartmentId,
                        url: fileUrl,
                        tag_id: state.tag_id
                    },
                })
            );

            const isSuccess = qrResult.meta.requestStatus === "fulfilled";

            if (isSuccess && embedded) {
                // Popup: clear the form, show the message under the button, refresh the list
                const createdName = state.qrCodeName;
                setState((prev) => ({
                    ...prev,
                    qrCodeName: "",
                    qrCodeKey: "",
                    branchDepartmentId: "",
                    tag_id: "",
                    errors: {},
                    open: false,
                }));
                setSuccessMsg("QR code created successfully");
                await dispatch(getQrCodeThunk({ type: "getQrCode" }));
                onSuccess?.(qrResult.payload?.message, { name: createdName });
                return;
            }

            setState((prev) => ({
                ...prev,
                open: true,
                message: isSuccess
                    ? qrResult.payload.message
                    : qrResult.payload || "QR code creation failed",
                severity: isSuccess ? "success" : "error",
            }));
            if (isSuccess) {
                await dispatch(getQrCodeThunk({ type: "getQrCode" }));
                onSuccess?.(qrResult.payload?.message, { name: state.qrCodeName, key: state.qrCodeKey });
            }
        } catch {
            setState((prev) => ({
                ...prev,
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            }));
        }
    };

    useEffect(() => {
        if (status === "failed")
            setState({ open: true, message: error, severity: "error" });
    }, [status, error]);

    // Icons shown at the start of each field
    const FIELD_ICONS = {
        qrCodeName: <LabelOutlinedIcon />,
        qrCodeKey: <VpnKeyOutlinedIcon />,
        branchDepartmentId: <ApartmentIcon />,
        tag_id: <LocalOfferOutlinedIcon />,
    };

    // Placeholder text inside each field
    const FIELD_PLACEHOLDERS = {
        qrCodeName: "Enter QR code name",
        qrCodeKey: "Enter keyword, e.g. ADMISSION",
        branchDepartmentId: "Select branch department",
        tag_id: "Select tag",
    };

    const panelSx = embedded
        ? { border: "none", boxShadow: "none", bgcolor: "transparent", height: "100%", overflow: "visible" } // popup: form sits directly in the dialog; visible so the success message is not clipped
        : { ...qrCardSx, height: "100%" };

    const isLoading = status === "loading";

    return (
        <>
            <Grid container spacing={2.5} sx={{ mt: embedded ? 0 : 0.5 }}>
                {/* ---------- Form ---------- */}
                <Grid size={12}>
                    <Card sx={panelSx}>
                        <CardContent sx={{ p: embedded ? "0 !important" : { xs: 2, md: 3 }, height: "100%", display: "flex", flexDirection: "column" }}>
                            {/* Fields: 2 × 2 */}
                            <Grid container columnSpacing={3} rowSpacing={3}>
                                {fields.map((field) => (
                                    <Grid key={field.id} size={{ xs: 12, sm: 6 }}>
                                        {field.type === "text" ? (
                                            <FieldTextField
                                                name={field.name}
                                                label={field.label}
                                                value={state[field.name]}
                                                onChange={handleChange}
                                                maxLength={20}
                                                icon={FIELD_ICONS[field.name]}
                                                placeholder={FIELD_PLACEHOLDERS[field.name]}
                                                required
                                                error={state.errors?.[field.name]}
                                            />
                                        ) : (
                                            <FieldSelect
                                                name={field.name}
                                                label={field.label}
                                                options={field.options}
                                                value={state[field.name]}
                                                onChange={handleChange}
                                                disabled={field.disabled}
                                                placeholder={FIELD_PLACEHOLDERS[field.name]}
                                                icon={FIELD_ICONS[field.name]}
                                                required
                                                error={state.errors?.[field.name]}
                                            />
                                        )}
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Footer */}
                            <Box sx={{ flex: 1 }} />
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="flex-end"
                                spacing={1.5}
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ position: "relative", mt: 4, pt: 2.5, borderTop: `1px solid ${QR.border}` }}
                            >
                                {onCancel && (
                                    <Button
                                        onClick={onCancel}
                                        disabled={isLoading}
                                        sx={{
                                            height: 44,
                                            px: 2.5,
                                            borderRadius: 3,
                                            textTransform: "none",
                                            fontSize: 14.5,
                                            fontWeight: 700,
                                            color: "#475569",
                                            border: "1.5px solid #e2e8f0",
                                            "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                )}
                                <QrActionButton
                                    variant="solid"
                                    icon={<QrCode2Icon sx={{ fontSize: 17 }} />}
                                    label="Create QR Code"
                                    loading={isLoading}
                                    loadingLabel="Generating..."
                                    onClick={handleSubmit}
                                />

                                {/* Success message: floats just below the button, so the popup layout never moves */}
                                {successMsg && (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={0.75}
                                        role="status"
                                        sx={{
                                            position: "absolute",
                                            top: "100%",
                                            right: 0,
                                            mt: 0.75,
                                            ml: "0 !important",
                                            whiteSpace: "nowrap",
                                            pointerEvents: "none",
                                            color: "#15803d",
                                            "@keyframes qrMsgIn": {
                                                from: { opacity: 0, transform: "translateY(-4px)" },
                                                to: { opacity: 1, transform: "translateY(0)" },
                                            },
                                            animation: "qrMsgIn .25s ease-out",
                                        }}
                                    >
                                        <CheckCircleIcon sx={{ fontSize: 17 }} />
                                        <Typography variant="body2" fontWeight={700}>
                                            {successMsg}
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <SnackbarMessage open={state.open} message={state.message} severity={state.severity} onClose={() => setState(prev => ({ ...prev, open: false }))} />
        </>
    );
}

// Standalone /qr-creation page (kept for direct links)
export default function QRCodeCreation() {
    const navigate = useNavigate();
    return (
        <>
            <Tabs />
            <Box sx={{ mt: 2 }}>
                <QrCreateForm onSuccess={() => navigate(APP_ROUTES.QRCODE.LIST)} />
            </Box>
        </>
    );
}
